// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import NotificationsPanelSideBarTemplate from '@front/templates/NotificationsPanelSideBarTemplate.html';
import { DataCache } from '@shared/commons/DataCache';
import { HistoricalBM } from '@shared/commons/cache/HistoricalBM';
import { ControlsTypes } from '../../../UtilsClasses/FactoryConstants';
import { HtmlScroll } from '@components/webdesktop/elements/Scroll/HtmlScroll';
import { LocalStorage } from '@shared/commons/LocalStorage';
import { BrokerMessageResponseType } from '@shared/utils/Notifications/BrokerMessageResponseType';
import { UrlUtils } from '@shared/utils/UrlUtils';
import { PanelSideBarBase } from './PanelSideBarBase';
import { Resources } from '@shared/localizations/Resources';
import { ThemeManager } from '../../../misc/ThemeManager';
export class NotificationsPanelSideBar extends PanelSideBarBase {
    constructor() {
        super();
        this.customButtonsClickEventHandlers = [];
    }
    oncomplete() {
        super.oncomplete();
        this.eventsSubscribe();
        this.GetBMHistory();
        this.scrollCardAdd();
        this.on('onCardClick', this.cardClick);
        this.on('categoryClick', this.categoryClick);
        this.observe('categories', this.onCategoriesChanged);
    }
    onteardown() {
        this.eventsUnSubscribe();
    }
    cardClick(event, item) {
        if (isNullOrUndefined(item)) {
            return;
        }
        const selectedCardID = this.get('selectedCardID');
        if (selectedCardID !== item.ItemID) {
            this.changeSelectedCard(false);
            void this.set('selectedCardID', item.ItemID).then(() => {
                this.processWasReadResponse();
                this.createCustomButtonsClickEvents();
                this.changeSelectedCard(true);
                this.cacheSelectedCardNumber();
            });
        }
        else {
            this.changeSelectedCard(false);
        }
    }
    cacheSelectedCardNumber() {
        const cards = this.get('cards');
        const selected = this.getSelectedCard(cards);
        if (!isNullOrUndefined(selected)) {
            LocalStorage.setNotificationPanelSelectedCards(selected.ID);
        }
    }
    eventsSubscribe() {
        DataCache.OnProcessBrokerMessage.Subscribe(this.addBrokerMessage, this);
    }
    eventsUnSubscribe() {
        DataCache.OnProcessBrokerMessage.UnSubscribe(this.addBrokerMessage, this);
    }
    GetBMHistory() {
        const userID = DataCache.getUserID();
        DataCache.SendBrokerMessageHistoryRequest(userID).then((msg) => {
            this.createAllMessages(msg);
        });
    }
    addBrokerMessage(histBM) {
        if (histBM.ShowPopUp !== false) {
            histBM.IsUnread = false;
        }
        const bmToAdd = this.createHistoricalBM(histBM);
        this.AddBMHistory(bmToAdd); // #110883 need to add not only BROKER_MESSAGE_NOTIFICATION_TYPE but also BM of other types
    }
    AddBMHistory(BMHist) {
        if (!BMHist) {
            return;
        }
        let allMsgs = this.get('allMessages');
        if (!allMsgs) {
            allMsgs = [];
        } // #110909
        // this.setCardLineHeight([BMHist]); // #111241+
        allMsgs = [BMHist].concat(allMsgs);
        void this.set('allMessages', allMsgs).then(() => {
            this.createCategories();
        });
    }
    createAllMessages(msg) {
        if (msg === null || msg === void 0 ? void 0 : msg.length) {
            msg = msg[0];
            const histBMs = msg.HistoricalBMs;
            if (histBMs) {
                // сортуємо за датою щоб відображати спочатку нові
                histBMs.sort(function (a, b) {
                    return b.TimeStamp.getTime() - a.TimeStamp.getTime();
                });
                for (let i = 0; i < histBMs.length; i++) {
                    histBMs[i] = this.createHistoricalBM(histBMs[i]);
                }
                void this.set('allMessages', histBMs).then(() => {
                    this.createCategories();
                });
            }
        }
    }
    createHistoricalBM(histBMInfo) {
        let result = histBMInfo;
        if (!histBMInfo.InstanceOfHistoricalBM) {
            const bm = new HistoricalBM();
            bm.UpdateByInfo(histBMInfo);
            result = bm;
        }
        return result;
    }
    createCategories() {
        const categories = [];
        const allMessages = this.get('allMessages');
        if (!allMessages) {
            return;
        }
        const catObj = {};
        const colorSet = [NotificationsPanelSideBar.NO_CATEGORY_COLOR]; // ['#101720'] //<- #110923 // */['#FF2D55', '#FF9500', '#4CD964', '#007AFF', '#EA606F', '#00FFFF', '#FC5BAC', '#33EEAA', '#FF4224', '#5500FF', '#49A191']
        let colorInd = -1;
        let hasAnyCardWithoutCategory = false; // чи є BMs без назначеної категорії(продукту)
        for (let i = 0; i < allMessages.length; i++) {
            const msg = allMessages[i];
            if (!msg)
                continue; // #110909
            const catName = msg.Category; // aka product.Name
            if (!catName) {
                // у нотіка нема продукту -> знач в нього нема категорії в тулбарі та сірий колір
                hasAnyCardWithoutCategory = true;
            }
            if (catName && !catObj[catName]) {
                const product = DataCache.EntitlementManager.ProductsCache[msg.ProductID];
                const colorInProduct = product ? product.ColorHEX : null;
                const validColor = this.isValidHexColor(colorInProduct);
                const color = validColor ? colorInProduct : colorSet[++colorInd];
                catObj[catName] = true;
                categories.push(new NotificationCategory(catName, color, true));
                if (colorInd + 1 === colorSet.length) {
                    colorInd = -1;
                }
            }
        }
        categories.sort(NotificationCategory.sorting);
        const needAllCategory = hasAnyCardWithoutCategory || categories.length > 1;
        if (needAllCategory) {
            /* Add "All" category per analyst’s comment: #110919 */
            categories.unshift(new NotificationCategory('All', NotificationsPanelSideBar.NO_CATEGORY_COLOR, true, true));
        }
        void this.set('categories', categories).then(() => {
            const showCategoryToolbar = categories.length > 1 || (!hasAnyCardWithoutCategory && categories.length > 0);
            void this.set({ showCategoryToolbar });
            this.createCategoriesColorObject();
            this.prepareVisibleCategories();
            const selectedCategory = this.get('selectedCategory');
            this.selectCategory(selectedCategory === null ? 0 : selectedCategory); // #110919, #111226
            this.prepareCardStyle();
        });
    }
    categoryClick(event, index) {
        if (this.get('selectedCategory') !== index) {
            this.changeSelectedCard(false);
            this.selectCategory(index);
        }
    }
    changeSelectedCard(param) {
        const cards = this.get('cards');
        const selected = this.getSelectedCard(cards);
        if (isNullOrUndefined(selected)) {
            return;
        }
        selected.IsSelected = param;
        void this.set('cards', cards);
        if (param) {
            void this.set('selectedCard', selected);
            this.createCustomButtonsClickEvents();
            this.find('iframe').onload = () => {
                const iframe = this.find('iframe');
                const iframeDocument = iframe.contentWindow.document;
                const iframeBody = iframeDocument.body;
                iframeBody.style.fontFamily = 'Trebuchet MS';
                iframeBody.style.fontStyle = 'normal';
                iframeBody.style.color = ThemeManager.colors.StandartForeground;
            };
        }
        else {
            void this.set({
                selectedCard: null,
                selectedCardID: null
            });
        }
    }
    selectCategory(index) {
        const categories = this.get('categories');
        if (isNullOrUndefined(categories) || categories.length === 0) {
            return;
        }
        const selectedCategory = categories[index];
        const selectedCategoryName = selectedCategory.name;
        const addAll = index === 0; // selectedCategoryName === 'All',
        const allMessages = this.get('allMessages');
        const cards = [];
        for (let i = 0; i < allMessages.length; i++) {
            const msg = allMessages[i];
            if (addAll || msg.Category === selectedCategoryName) {
                cards.push(msg);
            }
        }
        void this.set({
            cards,
            selectedCategory: index
        });
    }
    prepareVisibleCategories() {
        if (this.get('visibleCatIndex') === null) {
            void this.set('visibleCatIndex', 0);
        }
        this.updateToolbarCategoriesNum();
    }
    updateToolbarCategoriesNum() {
        const categories = this.get('categories');
        if (isNullOrUndefined(categories)) {
            return;
        }
        void this.set('visibleCatNum', true);
    }
    isValidHexColor(hexStr) {
        if (!hexStr || hexStr.length !== 7 || hexStr[0] !== '#') {
            return false;
        }
        return true;
    }
    createCategoriesColorObject() {
        const categories = this.get('categories');
        if (isNullOrUndefined(categories)) {
            return;
        }
        const result = {};
        for (let i = 0; i < categories.length; i++) {
            const c = categories[i];
            if (!result[c.name]) {
                result[c.name] = c.colorHEXstr;
            }
        }
        result[''] = NotificationsPanelSideBar.NO_CATEGORY_COLOR; // немає прив'язаного продукту
        void this.set('categoriesColor', result);
    }
    prepareCardStyle() {
        const allMessages = this.get('allMessages');
        if (isNullOrUndefined(allMessages)) {
            return;
        }
        const categoriesColor = this.get('categoriesColor');
        for (const card of allMessages) {
            if (categoriesColor[card.Category]) {
                card.colorHEXstr = categoriesColor[card.Category];
            }
        }
        void this.set('allMessages', allMessages).then(() => {
            void this.set('cards', this.get('cards')).then(() => {
                // this.openCachedSelectedCard();
            });
        });
    }
    // private openCachedSelectedCard (): void {
    //     const selectedCardID = +LocalStorage.getNotificationPanelSelectedCards();
    //     if (selectedCardID) {
    //         void this.set('selectedCardID', selectedCardID);
    //         const cards = this.get('cards');
    //         for (let i = 0; i < cards.length; i++) {
    //             const card = cards[i];
    //             if (card.ID === selectedCardID) {
    //                 this.changeSelectedCard(true);
    //                 return;
    //             }
    //         }
    //     }
    // }
    onCategoriesChanged(newValue) {
        this.updateCategoryCounter();
    }
    processWasReadResponse() {
        const cards = this.get('cards');
        const selected = this.getSelectedCard(cards);
        if (!(selected === null || selected === void 0 ? void 0 : selected.IsUnread))
            return;
        selected.IsUnread = false;
        void this.set('cards', cards);
        this.updateCategoryCounter();
        this.prepareCardStyle();
        const userID = +DataCache.getUserID();
        const userName = DataCache.getUserName();
        const { ID: brokerMsgID, ClusterNode: clusterNode } = selected;
        DataCache.SendBrokerResponseMessage({
            brokerMsgID,
            value: '',
            userID,
            userName,
            responseType: BrokerMessageResponseType.WAS_READ,
            clusterNode
        });
    }
    updateCategoryCounter() {
        const categories = this.get('categories');
        const allMessages = this.get('allMessages');
        if (!categories || !allMessages)
            return;
        const categoriesNum = categories.map((cat, j) => {
            return allMessages.reduce((count, msg) => {
                return count + (msg.IsUnread && (msg.Category === cat.name || j === 0) ? 1 : 0);
            }, 0);
        });
        void this.set('unreadByCategory', categoriesNum);
    }
    getSelectedCard(cards) {
        const selectedCardID = this.get('selectedCardID');
        return cards.find((card) => card.ID === selectedCardID) || null;
    }
    createCustomButtonsClickEvents() {
        this.cancelOldCustomButtonsClickEvent(); // #110916
        const cards = this.get('cards');
        const selectedCard = this.getSelectedCard(cards);
        if (!(selectedCard === null || selectedCard === void 0 ? void 0 : selectedCard.CustomButtons))
            return;
        this.customButtonsClickEventHandlers = selectedCard.CustomButtons.map((btn, i) => this.on(`onCustomButton${i}Click`, () => {
            UrlUtils.openFixedURL(btn.URL);
            this.processResponseCustomButtons(btn.TitleForResponse, i);
        }));
    }
    cancelOldCustomButtonsClickEvent() {
        var _a;
        (_a = this.customButtonsClickEventHandlers) === null || _a === void 0 ? void 0 : _a.forEach((handler) => handler.cancel());
        this.customButtonsClickEventHandlers = [];
    }
    processResponseCustomButtons(cBtnTitle, _clickedIndex) {
        const selCard = this.get('selectedCard');
        if (!selCard)
            return;
        const { ID: brokerMsgID, ClusterNode: clusterNode } = selCard;
        const userID = +DataCache.getUserID();
        const userName = DataCache.getUserName();
        DataCache.SendBrokerResponseMessage({
            brokerMsgID,
            value: cBtnTitle,
            userID,
            userName,
            responseType: BrokerMessageResponseType.CUSTOM_BTNS,
            clusterNode
        });
    }
    getType() {
        return ControlsTypes.NotificationsPanelSideBar;
    }
    scrollCardAdd() {
        const cardsItemContainer = this.find('.notificationsPanelSideBar-container-body-left');
        if (!isNullOrUndefined(cardsItemContainer)) {
            HtmlScroll.addScroll(cardsItemContainer);
        }
    }
    localize() {
        void this.set({
            noDataText: Resources.getResource('panel.Notifications.noNotificationsText')
        });
    }
}
NotificationsPanelSideBar.NO_CATEGORY_COLOR = '#505050';
PanelSideBarBase.extendWith(NotificationsPanelSideBar, {
    template: NotificationsPanelSideBarTemplate,
    data: function () {
        return {
            allMessages: null, // [] of HistoricalBrokerMessageInfo
            cards: null, // [] of HistoricalBM
            categories: null, // [] - all
            showCategoryToolbar: false, // boolean - whether to show categories in the toolbar
            unreadByCategory: null, // [] - кількість непрочитаних за категоріями (значення у лічильниках у тулбарі) в тому ж порядку
            visibleCatIndex: null, // int - first current index of categories that toolbar start with
            visibleCatNum: null, // int - number of categories visible in toolbar at the moment
            categoriesColor: null, // {'categories[i].name': categories[i].colorHEXstr}
            selectedCategory: null, // int
            selectedCardID: null, // int
            selectedCard: null, // object card
            noDataImg: 'notificationsBtn'
        };
    },
    computed: {
        headerBodyRightOpened: {
            get: function () {
                return this.get('selectedCard') === null ? '' : 'notificationsPanelSideBar-container-header-opened';
            }
        }
    }
});
class NotificationCategory {
    constructor(name, color, visible, isAllCategory = false) {
        this.name = 'All';
        this.colorHEXstr = NotificationsPanelSideBar.NO_CATEGORY_COLOR;
        this.visible = true;
        this.isAllCategory = false;
        this.name = name;
        this.colorHEXstr = color;
        this.visible = visible;
        this.isAllCategory = isAllCategory;
    }
    static sorting(a, b) {
        return a.name.localeCompare(b.name);
    }
}
